// P-51 (D-224 s.1, s.3-s.6) - the lead's road. Pure model: no React, no i18n.
//
// Seven circles: ליד חדש -> פגישה לפני אפיון -> הצעת מחיר לאפיון -> חתימה -> פגישת אפיון ->
// מסמכי אפיון והצעה לבנייה -> חתימה = לקוח. The conversion is the signature on the characterization
// quote (step 4); steps 5-7 are the customer's characterization card, shown here as a shortcut.
// "נוצר קשר" is optional: no circle, history only.

export const JOURNEY_STEPS = [
  'lead', 'preMeeting', 'charQuote', 'charSigned', 'charMeeting', 'buildQuote', 'buildSigned',
] as const;
export type JourneyStep = (typeof JOURNEY_STEPS)[number];
export type StepState = 'done' | 'current' | 'todo';

/** The index of the first step of the characterization card (the conversion point, s.1). */
export const CARD_FIRST_STEP = JOURNEY_STEPS.indexOf('charMeeting');

export interface JourneyMeetingFact { id: string; title?: string | null; meeting_datetime: string; status: string; meeting_type?: string | null }
export interface JourneyQuoteFact { id: string; quote_number?: number | string; title?: string | null; status: string; quote_type?: string; signed_at?: string | null }

/** The server's facts (GET /api/leads/:id/journey; the card's `journey` carries the last two). */
export interface LeadJourneyFacts {
  lead_status: string;
  stage_since?: string | null;
  days_in_stage?: number;
  contacted?: boolean;
  pre_meeting?: JourneyMeetingFact | null;
  characterization_quote?: JourneyQuoteFact | null;
  converted_to_org_id?: string | null;
  converted_at?: string | null;
  characterization_id?: string | null;
  characterization_meeting?: JourneyMeetingFact | null;
  build_quote?: JourneyQuoteFact | null;
}

const OPEN_MEETING = new Set(['scheduled', 'rescheduled']);
/** A quote "went out" (sent to the customer, or further). */
const QUOTE_OUT = new Set(['sent', 'viewed', 'accepted']);

export const meetingOpen = (m: JourneyMeetingFact | null | undefined): boolean => !!m && OPEN_MEETING.has(m.status);
export const meetingHeld = (m: JourneyMeetingFact | null | undefined): boolean => !!m && m.status === 'completed';
export const quoteSigned = (q: JourneyQuoteFact | null | undefined): boolean => !!q && (!!q.signed_at || q.status === 'accepted');
export const quoteOut = (q: JourneyQuoteFact | null | undefined): boolean => !!q && (QUOTE_OUT.has(q.status) || !!q.signed_at);

/** The meeting's time has come ("הפגישה התקיימה" shows only after it, s.4). */
export function meetingTimePassed(m: JourneyMeetingFact | null | undefined, now: Date = new Date()): boolean {
  if (!m) return false;
  const t = new Date(m.meeting_datetime).getTime();
  return Number.isFinite(t) && t <= now.getTime();
}

const LEAD_RANK: Record<string, number> = {
  new: 0, contacted: 0, meeting_scheduled: 1, qualified: 2, proposal_sent: 3, converted: 4,
};

/** How many of the seven steps are done. */
export function doneCount(f: LeadJourneyFacts): number {
  const converted = f.lead_status === 'converted' || !!f.converted_to_org_id;
  if (converted) {
    if (quoteSigned(f.build_quote)) return 7;
    if (quoteOut(f.build_quote)) return 6;
    if (meetingHeld(f.characterization_meeting)) return 5;
    return 4;
  }
  const rank = LEAD_RANK[f.lead_status] ?? 0;
  // The status is the source; the facts can only push it forward (a held meeting, a sent quote).
  // A signed characterization quote on a lead that did not convert (the server stopped for review): step 4 is done.
  if (quoteSigned(f.characterization_quote) && f.lead_status !== 'lost') return 4;
  let n = 0;
  if (rank >= 1 || f.pre_meeting) n = 1;
  if (rank >= 2 || meetingHeld(f.pre_meeting)) n = 2;
  if (rank >= 3 || quoteOut(f.characterization_quote)) n = 3;
  return n;
}

export interface StepView { key: JourneyStep; state: StepState }

/** The seven circles: ✓ done, the current one stressed, the rest grey. A closed lead has no current step. */
export function journeySteps(f: LeadJourneyFacts): StepView[] {
  const n = doneCount(f);
  const closed = f.lead_status === 'lost';
  return JOURNEY_STEPS.map((key, i) => ({
    key,
    state: i < n ? 'done' : i === n && !closed ? 'current' : 'todo',
  }));
}

/** The index of the current step (0-based; 7 = all done). */
export const currentStepIndex = (f: LeadJourneyFacts): number => doneCount(f);

/** What the "השלב הבא" box offers (s.4). The page turns each kind into the existing window / link. */
export type NextAction =
  | { kind: 'scheduleMeeting' }                                       // קביעת פגישה (pre_characterization)
  | { kind: 'meetingPlanned'; meeting: JourneyMeetingFact }           // the meeting is set, its time has not come
  | { kind: 'meetingHeld'; meeting: JourneyMeetingFact }              // "הפגישה התקיימה"
  | { kind: 'createCharQuote' }                                       // הצעת מחיר לאפיון (characterization, linked)
  | { kind: 'sendCharQuote'; quote: JourneyQuoteFact }                // a draft: open it to send
  | { kind: 'charQuoteSigned'; quote: JourneyQuoteFact }              // "ההצעה נחתמה" + link
  | { kind: 'needsConversionReview'; quote: JourneyQuoteFact }       // signed, but the conversion stopped for review
  | { kind: 'scheduleCharMeeting' }                                   // קביעת פגישת אפיון (the customer)
  | { kind: 'charMeetingPlanned'; meeting: JourneyMeetingFact }
  | { kind: 'charMeetingHeld'; meeting: JourneyMeetingFact }
  | { kind: 'buildQuoteAndDocs'; quote: JourneyQuoteFact | null }     // הצעה לבנייה + מסמכי אפיון
  | { kind: 'buildQuoteSigned'; quote: JourneyQuoteFact }             // "ההצעה נחתמה"
  | { kind: 'customer' }                                              // "הליד הפך ללקוח" + the customer page
  | { kind: 'closed' };                                               // "לא רלוונטי": no next step

export function nextAction(f: LeadJourneyFacts, now: Date = new Date()): NextAction {
  if (f.lead_status === 'lost') return { kind: 'closed' };
  const n = doneCount(f);
  const converted = f.lead_status === 'converted' || !!f.converted_to_org_id;
  if (!converted && quoteSigned(f.characterization_quote)) {
    return { kind: 'needsConversionReview', quote: f.characterization_quote as JourneyQuoteFact };
  }
  const meetingStep = (m: JourneyMeetingFact | null | undefined, held: 'meetingHeld' | 'charMeetingHeld',
    planned: 'meetingPlanned' | 'charMeetingPlanned', schedule: 'scheduleMeeting' | 'scheduleCharMeeting'): NextAction => {
    if (m && meetingOpen(m)) return meetingTimePassed(m, now) ? { kind: held, meeting: m } : { kind: planned, meeting: m };
    return { kind: schedule };
  };
  switch (n) {
    case 0: return meetingStep(f.pre_meeting, 'meetingHeld', 'meetingPlanned', 'scheduleMeeting');
    case 1: return meetingStep(f.pre_meeting, 'meetingHeld', 'meetingPlanned', 'scheduleMeeting');
    case 2: {
      const q = f.characterization_quote;
      return q && q.status === 'draft' ? { kind: 'sendCharQuote', quote: q } : { kind: 'createCharQuote' };
    }
    case 3: {
      const q = f.characterization_quote;
      return q ? { kind: 'charQuoteSigned', quote: q } : { kind: 'createCharQuote' };
    }
    case 4: return meetingStep(f.characterization_meeting, 'charMeetingHeld', 'charMeetingPlanned', 'scheduleCharMeeting');
    case 5: {
      const q = f.build_quote ?? null;
      return { kind: 'buildQuoteAndDocs', quote: q };
    }
    case 6: return f.build_quote ? { kind: 'buildQuoteSigned', quote: f.build_quote } : { kind: 'buildQuoteAndDocs', quote: null };
    default: return { kind: 'customer' };
  }
}

/** s.6 - "בשלב הזה כבר N ימים" after a week in the same status (only while the lead is in play). */
export const STUCK_DAYS = 7;
export function stuckDays(f: LeadJourneyFacts): number | null {
  if (f.lead_status === 'lost' || f.lead_status === 'converted') return null;
  const d = Number(f.days_in_stage ?? 0);
  return d >= STUCK_DAYS ? d : null;
}

/**
 * P-55b (D-230 s.1) - clicking a circle moves the lead to the FIRST status of that step. Only the steps
 * 1-4 are in the lead's hands; 5-7 are the customer's card, and "חתימה = לקוח" only through "ההצעה נחתמה".
 */
const STEP_STATUS: Partial<Record<JourneyStep, string>> = {
  lead: 'new', preMeeting: 'meeting_scheduled', charQuote: 'qualified', charSigned: 'proposal_sent',
};
export function stepTargetStatus(key: JourneyStep): string | null {
  return STEP_STATUS[key] ?? null;
}
/** A circle is pickable on an open (not closed, not converted) lead, when it is not the current step. */
export function stepPickable(f: LeadJourneyFacts, key: JourneyStep): boolean {
  if (f.lead_status === 'lost' || f.lead_status === 'converted' || f.converted_to_org_id) return false;
  const target = stepTargetStatus(key);
  if (!target) return false;
  return JOURNEY_STEPS.indexOf(key) !== currentStepIndex(f);
}

/** The phone line: "שלב N מתוך 7" (N = the current step, 1-based; capped at 7). */
export function phoneStepNumber(f: LeadJourneyFacts): number {
  return Math.min(JOURNEY_STEPS.length, currentStepIndex(f) + 1);
}
